import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { RadioGroup, RadioGroupItem, RadioGroupItemCard } from '@1trading/react/components/ui/radio-group';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useGuardedFormStepper } from '@/components/ui/guarded-form-stepper';
import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import { BINARY_ANSWERS } from '@/constants/answers';
import type { InstitutionBranchOperations, InstitutionBranchOperationsOrDefaults } from '@/features/institution/branch-operations/schema';
import type { FormStepGuard } from '@/hooks/use-form-step-guard';
import { usePromise } from '@/hooks/use-promise';

const InstitutionBranchOperationsFieldsetDifferentJurisdictionLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('institutionDetails.branchOperations.differentJurisdiction.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('institutionDetails.branchOperations.differentJurisdiction.description')}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
InstitutionBranchOperationsFieldsetDifferentJurisdictionLegend.displayName = 'InstitutionBranchOperationsFieldsetDifferentJurisdictionLegend';

const InstitutionBranchOperationsFieldsetDifferentJurisdiction = () => {
  const form = useFormContext<InstitutionBranchOperationsOrDefaults>();
  const { nextStepDeferred } = useGuardedFormStepper();
  const { isPending, handler: handleNextStep } = usePromise(nextStepDeferred);
  const { t } = useTranslation();

  return (
    <FormField
      name="differentJurisdiction"
      control={form.control}
      render={({ field }) => (
        <FormItem>
          <FormLabel className="sr-only">{t('institutionDetails.branchOperations.differentJurisdiction.description')}</FormLabel>
          <FormControl>
            <RadioGroup
              className="!mt-0"
              defaultValue={field.value}
              variant="cards"
              aria-label={t('institutionDetails.branchOperations.differentJurisdiction.description')}
              onValueChange={(value) => {
                field.onChange(value);
                if (value === 'NO') form.setValue('countryOfBranchOperations', undefined);
                handleNextStep();
              }}
            >
              {BINARY_ANSWERS.map((answer) => (
                <FormItem key={answer}>
                  <RadioGroupItemCard variant="outline">
                    <FormControl>
                      <RadioGroupItem value={answer} aria-disabled={isPending} />
                    </FormControl>
                    <FormLabel contained onClick={handleNextStep}>
                      {t(`fundamentals.${answer.toLowerCase()}`)}
                    </FormLabel>
                  </RadioGroupItemCard>
                </FormItem>
              ))}
            </RadioGroup>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
InstitutionBranchOperationsFieldsetDifferentJurisdiction.displayName = 'InstitutionBranchOperationsFieldsetDifferentJurisdiction';
InstitutionBranchOperationsFieldsetDifferentJurisdiction.isAutoNextStep = true;

const beforeStepLeave: FormStepGuard<InstitutionBranchOperations> = async ({ form, onSubmit }, next) => {
  if (!form) return next(false);

  if (form.getValues('differentJurisdiction') === 'NO') {
    await onSubmit(form.getValues());
    return next(false);
  }

  return next();
};

InstitutionBranchOperationsFieldsetDifferentJurisdiction.beforeStepLeave = beforeStepLeave;

export { InstitutionBranchOperationsFieldsetDifferentJurisdiction, InstitutionBranchOperationsFieldsetDifferentJurisdictionLegend };
